import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check } from "lucide-react";
import { cn } from "@nexus/ui";
import type {
  AskNexusClarificationAnswer,
  AskNexusClarificationCard,
  AskNexusClarificationQuestion,
} from "@/features/ask-nexus/api/ask-nexus.api";
import {
  answerLines,
  answerValue,
  missingRequired,
  optionLabel,
  periodPresets,
  presetLabel,
  toAnswers,
  type ClarificationDraft,
  type PeriodChoice,
} from "@/features/ask-nexus/lib/clarification";
import { ASK_NEXUS_ICON_STROKE } from "@/features/ask-nexus/lib/ui";

type ChoiceQuestion = Extract<AskNexusClarificationQuestion, { kind: "choice" }>;

const PRESETS = ["month_to_date", "last_30_days", "custom"] as const;

export interface ClarificationReply {
  /** Set once a later message answered the card: its choices, or null when the person typed the answer. */
  answered: { answers: AskNexusClarificationAnswer[] | null } | null;
  canAnswer: boolean;
  onAnswer: (message: string, answers: AskNexusClarificationAnswer[]) => void;
}

/**
 * Questions Ask Nexus asks as choices. Industries and brands are the options a
 * nexus_list_filter_options result of the same answer returned; periods are
 * Nexus presets. Choices stay in the card until Continue, which sends them as
 * the next message of this chat; an answered card only shows what was sent.
 */
export function AskNexusClarification({ card, reply }: { card: AskNexusClarificationCard; reply: ClarificationReply }) {
  return reply.answered ? <AnsweredCard answers={reply.answered.answers} /> : <OpenCard card={card} reply={reply} />;
}

function OpenCard({ card, reply }: { card: AskNexusClarificationCard; reply: ClarificationReply }) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<ClarificationDraft>({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const missing = missingRequired(card, draft);
  const isLocked = !reply.canAnswer || isSubmitted;
  useUnlockAfterFailedSend(reply.canAnswer, setIsSubmitted);

  const handleContinue = () => {
    const answers = toAnswers(t, card, draft);
    setIsSubmitted(true);
    reply.onAnswer(answerLines(t, answers).join("\n"), answers);
  };
  const update = (questionId: string, value: string[] | PeriodChoice) => setDraft((current) => ({ ...current, [questionId]: value }));

  return (
    <section
      className="rounded-2xl border border-(--an-hair) bg-(--an-field) p-4"
      aria-label={t("askNexus.cards.clarification.title", "Questions from Ask Nexus")}
      data-testid="ask-nexus-card-clarification"
    >
      {card.questions.map((question, index) => (
        // The divider sits on a wrapper: a fieldset draws its legend over its own top border.
        <div key={question.id} className={cn(index > 0 && "mt-4 border-t border-(--an-hair) pt-3.5")}>
          <fieldset disabled={isLocked} className="min-w-0">
            <legend className="flex w-full items-baseline justify-between gap-2 text-[13px] font-medium">
              <span>{question.label}</span>
              <span className="text-[11px] font-normal text-(--an-ink-3)">
                {question.isRequired ? t("askNexus.cards.clarification.required", "Required") : t("askNexus.cards.clarification.optional", "Optional")}
              </span>
            </legend>
            {question.kind === "choice" ? (
              <ChoiceField question={question} selected={(draft[question.id] as string[] | undefined) ?? []} onChange={(values) => update(question.id, values)} />
            ) : (
              <PeriodField value={draft[question.id] as PeriodChoice | undefined} onChange={(value) => update(question.id, value)} />
            )}
          </fieldset>
        </div>
      ))}
      <div className="mt-4 flex items-center justify-between gap-3">
        <p className="text-xs text-(--an-ink-2)" aria-live="polite">
          {missing.length > 0
            ? t("askNexus.cards.clarification.stillNeeded", "Answer the required questions to continue.")
            : t("askNexus.cards.clarification.orType", "Or type your answer below.")}
        </p>
        <button
          type="button"
          className="inline-flex h-[34px] flex-none items-center rounded-full bg-(--an-primary) px-4 text-xs font-medium text-(--an-primary-ink) disabled:opacity-35"
          disabled={isLocked || missing.length > 0}
          onClick={handleContinue}
        >
          {t("askNexus.cards.clarification.continue", "Continue")}
        </button>
      </div>
      {card.questions.some((question) => question.kind === "choice") ? (
        <p className="mt-3 text-[11px] text-(--an-ink-3)">
          {t("askNexus.cards.clarification.source", "Options from {{tool}}", { tool: "nexus_list_filter_options" })}
        </p>
      ) : null}
    </section>
  );
}

/** Continue locks the card; a send that fails hands the turn back, and the card opens again. */
function useUnlockAfterFailedSend(canAnswer: boolean, setIsSubmitted: (value: boolean) => void) {
  const couldAnswer = useRef(canAnswer);
  useEffect(() => {
    if (canAnswer && !couldAnswer.current) setIsSubmitted(false);
    couldAnswer.current = canAnswer;
  }, [canAnswer, setIsSubmitted]);
}

function ChoiceField({ question, selected, onChange }: { question: ChoiceQuestion; selected: string[]; onChange: (values: string[]) => void }) {
  const { t } = useTranslation();
  const name = useId();
  const listId = useId();
  const [isExpanded, setIsExpanded] = useState(false);
  const [search, setSearch] = useState("");
  const shown = useMemo(() => {
    const values = new Set([...question.suggested, ...selected]);
    return question.options.filter((option) => values.has(option.value));
  }, [question, selected]);
  const matches = question.options.filter((option) => optionLabel(t, question, option).toLowerCase().includes(search.trim().toLowerCase()));
  const toggle = (value: string) => {
    if (!question.isMultiple) return onChange([value]);
    onChange(selected.includes(value) ? selected.filter((item) => item !== value) : [...selected, value]);
  };
  const inputType = question.isMultiple ? "checkbox" : "radio";

  if (question.options.some((option) => option.description)) {
    return (
      <div className="mt-2 flex flex-col gap-1.5">
        {question.options.map((option) => (
          <DescribedOption
            key={option.value}
            type={inputType}
            name={name}
            value={option.value}
            checked={selected.includes(option.value)}
            onChange={() => toggle(option.value)}
            label={optionLabel(t, question, option)}
            description={option.description}
          />
        ))}
      </div>
    );
  }

  if (isExpanded) {
    return (
      <div className="mt-2">
        <label className="sr-only" htmlFor={`${listId}-search`}>
          {t("askNexus.cards.clarification.search", "Search {{count}} options", { count: question.options.length })}
        </label>
        <input
          id={`${listId}-search`}
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t("askNexus.cards.clarification.search", "Search {{count}} options", { count: question.options.length })}
          className="h-8 w-full rounded-[10px] bg-(--an-inset) px-2.5 text-xs outline-none placeholder:text-(--an-ink-3) focus-visible:ring-2 focus-visible:ring-(--an-info-ink)"
        />
        <ul id={listId} className="mt-1.5 max-h-44 overflow-y-auto rounded-xl border border-(--an-hair)">
          {matches.map((option) => (
            <li key={option.value} className="border-b border-(--an-hair) last:border-b-0">
              <label className="flex cursor-pointer items-center gap-2 px-2.5 py-1.5 text-xs">
                <input
                  type={inputType}
                  name={name}
                  value={option.value}
                  checked={selected.includes(option.value)}
                  onChange={() => toggle(option.value)}
                  className="accent-(--an-primary)"
                />
                {optionLabel(t, question, option)}
              </label>
            </li>
          ))}
        </ul>
        <div className="mt-1.5 flex items-start justify-between gap-2 text-[11px] text-(--an-ink-3)">
          <p>
            {question.isPartial
              ? t("askNexus.cards.clarification.partial", "Showing the first {{count}} of {{total}} Nexus returned. Not listed? Type the name below.", {
                  count: question.options.length,
                  total: question.total,
                })
              : t("askNexus.cards.clarification.notListed", "Not listed? Type the name below.")}
          </p>
          <button type="button" className="flex-none underline-offset-2 hover:underline" aria-controls={listId} aria-expanded onClick={() => setIsExpanded(false)}>
            {t("askNexus.cards.clarification.showFewer", "Show fewer")}
          </button>
        </div>
      </div>
    );
  }
  return (
    <div className="mt-2 flex flex-wrap gap-1.5">
      {shown.map((option) => (
        <ChipInput
          key={option.value}
          type={inputType}
          name={name}
          value={option.value}
          checked={selected.includes(option.value)}
          onChange={() => toggle(option.value)}
          label={optionLabel(t, question, option)}
        />
      ))}
      {question.options.length > shown.length || question.isPartial ? (
        <button
          type="button"
          className="inline-flex h-[30px] items-center rounded-full border border-dashed border-(--an-hair-dashed) px-3 text-xs text-(--an-ink-2) focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--an-info-ink) disabled:opacity-60"
          aria-controls={listId}
          aria-expanded={false}
          onClick={() => setIsExpanded(true)}
        >
          {t("askNexus.cards.clarification.showAll", "Show all {{count}}", { count: question.total })}
        </button>
      ) : null}
    </div>
  );
}

function PeriodField({ value, onChange }: { value: PeriodChoice | undefined; onChange: (value: PeriodChoice) => void }) {
  const { t } = useTranslation();
  const name = useId();
  const presets = useMemo(() => periodPresets(new Date()), []);
  const choose = (preset: (typeof PRESETS)[number]) =>
    onChange(preset === "custom" ? { preset, startDate: value?.startDate ?? "", endDate: value?.endDate ?? "" } : { preset, ...presets[preset] });

  return (
    <>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {PRESETS.map((preset) => (
          <ChipInput
            key={preset}
            type="radio"
            name={name}
            value={preset}
            checked={value?.preset === preset}
            onChange={() => choose(preset)}
            label={presetLabel(t, preset)}
            description={preset === "custom" ? undefined : `${presets[preset].startDate} – ${presets[preset].endDate}`}
          />
        ))}
      </div>
      {value?.preset === "custom" ? (
        <div className="mt-2 flex items-center gap-2 rounded-xl bg-(--an-inset) px-2.5 py-2">
          <DateInput label={t("askNexus.cards.clarification.from", "From")} value={value.startDate} max={presets.month_to_date.endDate} onChange={(startDate) => onChange({ ...value, startDate })} />
          <span className="text-xs text-(--an-ink-2)" aria-hidden="true">
            {t("askNexus.cards.clarification.to", "to")}
          </span>
          <DateInput label={t("askNexus.cards.clarification.until", "To")} value={value.endDate} max={presets.month_to_date.endDate} onChange={(endDate) => onChange({ ...value, endDate })} />
        </div>
      ) : null}
      <p className="mt-1.5 text-[11px] text-(--an-ink-3)">
        {t("askNexus.cards.clarification.utc", "Dates on the UTC calendar, as in the rest of Nexus.")}
      </p>
    </>
  );
}

function DateInput({ label, value, max, onChange }: { label: string; value: string; max: string; onChange: (value: string) => void }) {
  return (
    <input
      type="date"
      aria-label={label}
      value={value}
      max={max}
      onChange={(event) => onChange(event.target.value)}
      className="h-[30px] min-w-0 flex-1 rounded-lg border border-(--an-hair-strong) bg-(--an-field) px-2.5 text-xs tabular-nums"
    />
  );
}

function ChipInput({
  type,
  name,
  value,
  checked,
  onChange,
  label,
  description,
}: {
  type: "radio" | "checkbox";
  name: string;
  value: string;
  checked: boolean;
  onChange: () => void;
  label: string;
  description?: string;
}) {
  return (
    <label
      className="inline-flex h-[30px] cursor-pointer items-center rounded-full border border-(--an-hair-strong) bg-(--an-field) px-3 text-xs has-[:checked]:border-(--an-primary) has-[:checked]:bg-(--an-primary) has-[:checked]:text-(--an-primary-ink) has-[:disabled]:cursor-default has-[:disabled]:opacity-60 has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-(--an-info-ink)"
      title={description}
    >
      <input type={type} name={name} value={value} checked={checked} onChange={onChange} className="sr-only" aria-description={description} />
      {label}
    </label>
  );
}

/** A choice whose description matters to the decision, such as a report type: the description stays visible. */
function DescribedOption({
  type,
  name,
  value,
  checked,
  onChange,
  label,
  description,
}: {
  type: "radio" | "checkbox";
  name: string;
  value: string;
  checked: boolean;
  onChange: () => void;
  label: string;
  description?: string;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-2.5 rounded-xl border border-(--an-hair-strong) bg-(--an-field) px-3 py-2.5 has-[:checked]:border-(--an-primary) has-[:disabled]:cursor-default has-[:disabled]:opacity-60 has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-(--an-info-ink)">
      <input type={type} name={name} value={value} checked={checked} onChange={onChange} className="mt-0.5 accent-(--an-primary)" />
      <span className="flex flex-col gap-0.5">
        <span className="text-[13px] font-medium">{label}</span>
        {description ? <span className="text-xs text-(--an-ink-2)">{description}</span> : null}
      </span>
    </label>
  );
}

function AnsweredCard({ answers }: { answers: AskNexusClarificationAnswer[] | null }) {
  const { t } = useTranslation();
  return (
    <section className="rounded-2xl border border-(--an-hair) bg-(--an-field) p-4" data-testid="ask-nexus-card-clarification">
      <p className="flex items-center gap-2 text-xs text-(--an-ink-2)">
        <span className="grid size-[18px] place-items-center rounded-full bg-(--an-neutral-tint)">
          <Check className="size-3" strokeWidth={ASK_NEXUS_ICON_STROKE} aria-hidden="true" />
        </span>
        {answers ? t("askNexus.cards.clarification.answered", "Answered") : t("askNexus.cards.clarification.answeredInText", "Answered in your message below")}
      </p>
      {answers ? (
        // Each answer under its question, full width: a long question beside it would squeeze the answer to a word a line.
        <dl className="mt-2.5 flex flex-col gap-2.5 text-xs">
          {answers.map((answer) => (
            <div key={answer.questionId}>
              <dt className="text-(--an-ink-2)">{answer.label}</dt>
              <dd className="mt-0.5 font-medium text-(--an-ink)">{answerValue(t, answer)}</dd>
            </div>
          ))}
        </dl>
      ) : null}
    </section>
  );
}
